// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the browser's side of `/api/situation` (Situation room,
 * docs/AYNI_XR_CONTROL_ROOM_PLAN.md; the shapes are in `situationRoom.ts`).
 * Members of a Situation Room licence read and change the room with their
 * session cookie; a paired wall display uses its own token, kept in this
 * browser only (it has no account, decision CR-D2).
 */

import type { Khipu } from './khipu'
import type { PairPoll, PairStart, SituationBriefing, SituationLayerInfo, SituationState, WallState } from './situationRoom'

export type SituationRefusal = 'unauthorized' | 'plan_required' | 'unavailable'

/** Answers that are not the thing asked for, by name; anything unexpected is `failed`. */
export type SituationError = SituationRefusal | 'invalid' | 'too_large' | 'too_many' | 'not_found' | 'too_many_features' | 'empty' | 'rate_limited' | 'failed'

const BASE = '/api/situation'

async function call<T>(path: string, init: RequestInit = {}, fetchImpl: typeof fetch = fetch): Promise<{ ok: true; body: T } | { ok: false; error: SituationError }> {
  let res: Response
  try {
    res = await fetchImpl(`${BASE}${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...(init.headers ?? {}) },
    })
  } catch {
    return { ok: false, error: 'unavailable' }
  }
  if (res.ok) {
    const body = res.status === 204 ? null : await res.json().catch(() => null)
    return { ok: true, body: body as T }
  }
  const error = await res.json().then((b: { error?: unknown }) => b?.error, () => undefined)
  if (res.status === 401) return { ok: false, error: 'unauthorized' }
  if (res.status === 402) return { ok: false, error: 'plan_required' }
  if (res.status === 404) return { ok: false, error: 'not_found' }
  if (res.status === 429) return { ok: false, error: 'rate_limited' }
  if (res.status === 503) return { ok: false, error: 'unavailable' }
  if (res.status === 413) return { ok: false, error: 'too_large' }
  if (typeof error === 'string' && ['invalid', 'too_large', 'too_many', 'too_many_features', 'empty'].includes(error)) return { ok: false, error: error as SituationError }
  return { ok: false, error: 'failed' }
}

const send = (method: string, body?: unknown): RequestInit => ({ method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) })

// ----------------------------------------------------------------- members

export const loadSituation = (f?: typeof fetch) => call<SituationState>('', {}, f)
export const saveBoard = (board: Khipu, f?: typeof fetch) => call<{ boardUpdatedAt: number }>('/board', send('PUT', { board }), f)
export const clearBoard = (f?: typeof fetch) => call<null>('/board', send('DELETE'), f)
export const claimDisplay = (code: string, name: string, f?: typeof fetch) => call<{ id: string }>('/displays', send('POST', { code, name }), f)
export const renameDisplay = (id: string, name: string, f?: typeof fetch) => call<null>(`/displays/${encodeURIComponent(id)}`, send('PATCH', { name }), f)
export const unpairDisplay = (id: string, f?: typeof fetch) => call<null>(`/displays/${encodeURIComponent(id)}`, send('DELETE'), f)
export const putBriefing = (b: SituationBriefing, f?: typeof fetch) => call<null>('/briefing', send('PUT', b), f)
export const clearBriefing = (f?: typeof fetch) => call<null>('/briefing', send('DELETE'), f)
export const uploadLayer = (title: string, geojson: unknown, f?: typeof fetch) =>
  call<{ id: string; features: number; bytes: number }>('/layers', send('POST', { title, geojson }), f)
export const renameLayer = (id: string, title: string, f?: typeof fetch) => call<null>(`/layers/${encodeURIComponent(id)}`, send('PATCH', { title }), f)
export const deleteLayer = (id: string, f?: typeof fetch) => call<null>(`/layers/${encodeURIComponent(id)}`, send('DELETE'), f)

/** How a page may read an own layer: as a member, as a paired wall, or as someone in a briefing meeting the room shows it to (CR-D14). */
export type LayerAccess = { kind: 'member' } | { kind: 'wall'; token: string } | { kind: 'meeting'; code: string }

export async function fetchLayer(id: string, access: LayerAccess, fetchImpl: typeof fetch = fetch): Promise<GeoJSON.FeatureCollection | null> {
  const headers: Record<string, string> = { Accept: 'application/geo+json' }
  let query = ''
  if (access.kind === 'wall') headers.Authorization = `Bearer ${access.token}`
  if (access.kind === 'meeting') query = `?meeting=${encodeURIComponent(access.code)}`
  try {
    const res = await fetchImpl(`${BASE}/layers/${encodeURIComponent(id)}${query}`, { headers, credentials: 'same-origin', cache: 'no-store' })
    if (!res.ok) return null
    const body = (await res.json()) as GeoJSON.FeatureCollection
    return body?.type === 'FeatureCollection' && Array.isArray(body.features) ? body : null
  } catch {
    return null
  }
}

// ------------------------------------------------- briefing meetings (CR-D14)

/** The presenter shows these of the room's own layers to the meeting (its seat token proves it presents). */
export const shareMeetingLayers = (code: string, seat: string, layers: readonly string[], f?: typeof fetch) =>
  call<{ layers: string[] }>('/meeting', send('PUT', { code, seat, layers }), f)
/** What a meeting is shown, by its code (anyone in it). */
export const loadMeetingLayers = (code: string, f?: typeof fetch) =>
  call<{ layers: SituationLayerInfo[] }>(`/meeting/${encodeURIComponent(code)}`, { cache: 'no-store' }, f)

// ------------------------------------------------------------- wall displays

export const startPairing = (f?: typeof fetch) => call<PairStart>('/pair', send('POST'), f)
export const pollPairing = (code: string, poll: string, f?: typeof fetch) =>
  call<PairPoll>(`/pair?code=${encodeURIComponent(code)}&poll=${encodeURIComponent(poll)}`, { cache: 'no-store' }, f)
export const loadWall = (token: string, f?: typeof fetch) =>
  call<WallState>('/wall', { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }, f)

/** What a paired wall keeps in this browser. */
export interface WallPairing {
  token: string
  displayId: string
  name: string
}

const WALL_KEY = 'ayni-wall-display'

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function storedWallPairing(store: Storage | null = storage()): WallPairing | null {
  try {
    const raw = JSON.parse(store?.getItem(WALL_KEY) ?? 'null') as Partial<WallPairing> | null
    if (raw && typeof raw.token === 'string' && typeof raw.displayId === 'string') return { token: raw.token, displayId: raw.displayId, name: typeof raw.name === 'string' ? raw.name : '' }
  } catch {
    /* unreadable: not paired */
  }
  return null
}

export function storeWallPairing(p: WallPairing | null, store: Storage | null = storage()): void {
  try {
    if (p) store?.setItem(WALL_KEY, JSON.stringify(p))
    else store?.removeItem(WALL_KEY)
  } catch {
    /* private mode: the wall pairs again after a reload */
  }
}
